/**
 * Temporary package executor detection utilities for Socket CLI.
 * Identifies and handles temporary execution contexts.
 *
 * Key Functions:
 * - isRunningInTemporaryExecutor: Detects if running in npx/dlx/exec context
 * - shouldSkipShadow: Determines if shadow installation should be skipped
 *
 * Temporary Execution Contexts:
 * - npm exec/npx: Runs packages in temporary npm cache
 * - pnpm dlx: Executes packages in temporary pnpm store
 * - yarn dlx: Runs packages in temporary yarn environment
 *
 * Detection Methods:
 * - Environment variable analysis (npm_config_user_agent)
 * - Path pattern matching for temporary directories
 * - Cache directory identification
 *
 * Usage:
 * - Prevents shadow installation in temporary contexts
 * - Avoids PATH pollution in ephemeral environments
 * - Ensures package manager commands work correctly
 */

import path from 'node:path'

import { normalizePath } from '@socketsecurity/registry/lib/path'

import constants from '../constants.mts'

/**
 * Detects if the current process is running in a temporary package execution context
 * such as npm exec, npx, pnpm dlx, or yarn dlx.
 *
 * When package managers run commands via exec/npx/dlx, they execute in temporary directories
 * that are cleaned up after execution. Creating persistent shadows or modifying PATH
 * in these contexts can break subsequent package manager commands.
 *
 * @returns true if running in an exec/npx/dlx context, false otherwise
 */
export function isRunningInTemporaryExecutor(): boolean {
  // Check environment variable for exec/npx/dlx indicators.
  const userAgent = constants.ENV.npm_config_user_agent
  if (
    userAgent?.includes('exec') ||
    userAgent?.includes('npx') ||
    userAgent?.includes('dlx')
  ) {
    return true
  }

  // Normalize the __dirname path for consistent checking across platforms.
  const normalizedDirname = normalizePath(__dirname)

  // Check if running from npm's npx cache.
  const npmCache = constants.ENV.npm_config_cache
  if (npmCache && normalizedDirname.includes(normalizePath(npmCache))) {
    return true
  }

  // Check common temporary execution path patterns.
  const tempPatterns = [
    '_npx', // npm's npx cache directory
    '.pnpm-store', // pnpm dlx temporary store
    'dlx-', // Common dlx directory prefix
    '.yarn/$$', // Yarn Berry PnP virtual packages
    path.sep === '\\'
      ? 'AppData\\Local\\Temp\\xfs-'
      : 'AppData/Local/Temp/xfs-', // Yarn on Windows
  ]

  return tempPatterns.some(pattern => normalizedDirname.includes(pattern))
}

export type ShadowInstallationOptions = {
  cwd?: string | undefined
  win32?: boolean | undefined
}

/**
 * Determines if shadow binaries should be installed.
 * Shadows should NOT be installed when:
 * - Running in a temporary execution context (exec/npx/dlx)
 * - On Windows with an existing binary path (required for Windows to function)
 *
 * @param binPath - Path to the binary being shadowed
 * @param options - Configuration options
 * @param options.cwd - Current working directory path to check
 * @param options.win32 - Whether running on Windows
 * @returns true if shadow installation should be skipped
 */
export function shouldSkipShadow(
  binPath: string,
  options: ShadowInstallationOptions,
): boolean {
  const { cwd = process.cwd(), win32 = false } = {
    __proto__: null,
    ...options,
  } as ShadowInstallationOptions

  // Windows compatibility: Skip shadow installation if binary is already found.
  //
  // This check is required because Windows handles executables differently than Unix:
  // 1. File locking - Windows locks running executables, so cmd-shim creation would
  //    fail with EBUSY/EACCES errors when trying to create wrapper files.
  // 2. PATH conflicts - Attempting to shadow an already-resolved binary can create
  //    circular references or ambiguous command resolution.
  // 3. Registry integration - Windows package managers often use system-level
  //    integrations beyond just PATH that our shadowing would interfere with.
  //
  // Without this check, users would see "Access Denied" or file locking errors
  // that are difficult to debug. This is not a performance optimization - the
  // shadow installation will fail without it.
  if (win32 && binPath) {
    return true
  }

  // Check environment variable for exec/npx/dlx indicators.
  const userAgent = constants.ENV.npm_config_user_agent
  if (
    userAgent?.includes('exec') ||
    userAgent?.includes('npx') ||
    userAgent?.includes('dlx')
  ) {
    return true
  }

  // Normalize the cwd path for consistent checking across platforms.
  const normalizedCwd = normalizePath(cwd)

  // Check if running from npm's npx cache.
  const npmCache = constants.ENV.npm_config_cache
  if (npmCache && normalizedCwd.includes(normalizePath(npmCache))) {
    return true
  }

  // Check common temporary execution path patterns.
  const tempPatterns = [
    '_npx', // npm's npx cache directory
    '.pnpm-store', // pnpm dlx temporary store
    'dlx-', // Common dlx directory prefix
    '.yarn/$$', // Yarn Berry PnP virtual packages
    path.sep === '\\'
      ? 'AppData\\Local\\Temp\\xfs-'
      : 'AppData/Local/Temp/xfs-', // Yarn on Windows
  ]

  return tempPatterns.some(pattern => normalizedCwd.includes(pattern))
}
